import Dialog from 'components/Dialog';
import React from 'react';
import {Grid} from '@mui/material';
import FormikTextField from 'components/inputs/FormikTextField';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import * as yup from 'common/yup';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {useDispatch, useSelector} from 'react-redux';
import {layoutActions} from 'features/layout/redux';
import {packId, unpackId} from 'common/diplomatIdUtil';
import {useTranslation} from 'react-i18next';
import {IDiplomatObject} from 'types/DiplomatObject';
import history from 'redux/history';
import {synchronizationsActions, synchronizationsSelectors} from 'redux/synchronizations';
import {ISynchronization} from 'types/diplomat/transaction/Synchronization';
import Formik from 'components/hook-form/Formik';

const validationSchema = yup.object({
   synchronizationName: yup.string().required(),
});

type Props = {
   data?: INav;
   onClose: () => void;
};

const SaveSynchronizationAsDialog = ({data, onClose}: Props) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();
   const synchronization = useSelector(synchronizationsSelectors.synchronizationsSelector);

   const {id, objectType} = unpackId(data?.id);
   const saveAs = React.useCallback(
      ({synchronizationName}) => {
         dispatch(
            synchronizationsActions.saveAs(id, objectType, synchronizationName, {
               onSuccess: (data: ISynchronization) => {
                  dispatch(layoutActions.loadTreeNav());
                  onClose();
                  history.push(`/${packId(data.objectType, data.id)}`);
               },
               onFailure: onClose,
            })
         );
      },
      [dispatch, id, objectType, onClose]
   );

   const synchronizationName = t('synchronization');

   const validateName = (value: string) => {
      const isNameExists = synchronization.some((obj: IDiplomatObject) => obj.name === value);
      if (isNameExists) {
         return t(`Diplomat already contains a {{synchronizationName}} named {{name}}`, {
            synchronizationName,
            name: value,
         });
      }
   };

   return (
      <Dialog maxWidth="sm" header={t('Save Synchronization As')} close={onClose}>
         <Formik
            initialValues={validationSchema.cast({synchronizationName: data?.name})}
            onSubmit={saveAs}
         >
            <>
               <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={8}>
                     <FormikTextField
                        autoFocus
                        type="text"
                        name="synchronizationName"
                        label={t('New {{synchronizationName}} Name:', {synchronizationName})}
                        required
                        validate={validateName}
                     />
                  </Grid>
               </Grid>
               <Grid item xs={12}>
                  <FormFooterButtons cancel={onClose} />
               </Grid>
            </>
         </Formik>
      </Dialog>
   );
};

export default SaveSynchronizationAsDialog;
